export const DEVEX_REVIEW_LATENCY_GROUP_BYS = [
  'team',
  'size',
  'relationship',
  'assignment',
  'hourOfDay',
  'dayOfWeek',
  'surface',
] as const;
export type DevexReviewLatencyGroupBy = (typeof DEVEX_REVIEW_LATENCY_GROUP_BYS)[number];

export const DEVEX_REVIEW_LATENCY_SIZE_BUCKETS = ['XS', 'S', 'M', 'L', 'XL'] as const;
export type DevexReviewLatencySizeBucket = (typeof DEVEX_REVIEW_LATENCY_SIZE_BUCKETS)[number];

export const DEVEX_REVIEW_LATENCY_GITHUB_TEAMS = [
  'acquisition',
  'activation',
  'analytics',
  'consumer',
  'labs',
  'medical',
  'ops-systems',
  'platform',
  'provider-quality',
  'provider-supply',
] as const;

export const DEVEX_REVIEW_LATENCY_TEAMS = [
  ...DEVEX_REVIEW_LATENCY_GITHUB_TEAMS,
  'growth',
  'provider',
  'other',
] as const;
export type DevexReviewLatencyTeam = (typeof DEVEX_REVIEW_LATENCY_TEAMS)[number];

// Keep historical parent-team attribution readable while collecting the current team breakdowns.
// Other is derived at aggregation time so historical facts with no attribution remain visible.
export function getDevexReviewLatencyTeams(authorTeams: readonly string[] = []): DevexReviewLatencyTeam[] {
  const teams = DEVEX_REVIEW_LATENCY_TEAMS.filter((team) => team !== 'other' && authorTeams.includes(team));
  return teams.length > 0 ? teams : ['other'];
}

export const DEVEX_REVIEW_AUTHOR_TEAM_RELATIONSHIPS = ['in-team', 'cross-team', 'no-team-tag'] as const;
export type DevexReviewAuthorTeamRelationship = (typeof DEVEX_REVIEW_AUTHOR_TEAM_RELATIONSHIPS)[number];

export const DEVEX_REVIEW_ASSIGNMENT_MODES = ['individual-only', 'team-only', 'mixed', 'none'] as const;
export type DevexReviewAssignmentMode = (typeof DEVEX_REVIEW_ASSIGNMENT_MODES)[number];

export const DEVEX_REVIEW_OPENED_HOUR_BUCKETS = ['business-hours', 'off-hours'] as const;
export type DevexReviewOpenedHourBucket = (typeof DEVEX_REVIEW_OPENED_HOUR_BUCKETS)[number];

export const DEVEX_REVIEW_OPENED_DAY_BUCKETS = ['mon-thu', 'friday', 'weekend'] as const;
export type DevexReviewOpenedDayBucket = (typeof DEVEX_REVIEW_OPENED_DAY_BUCKETS)[number];

export const DEVEX_REVIEW_TOUCH_SURFACES = ['server', 'client', 'mobile', 'shared', 'multi', 'other'] as const;
export type DevexReviewTouchSurface = (typeof DEVEX_REVIEW_TOUCH_SURFACES)[number];

// Persisted shape of a per-PR review fact row in DynamoDB. The infra devex-metrics publish lambda
// writes it and the dev-console server reads it, so this is the single source of truth for that
// cross-package contract — keep the writer and reader pointed at this type so they cannot drift.
export interface DevexReviewFactItem {
  pk: 'fact';
  sk: string;
  generatedAt: string;
  prNumber: number;
  openedAt: string;
  reviewedAt: string;
  timeToFirstHumanReviewSeconds: number;
  authorTeams: string[];
  requestedTeams: string[];
  sizeLoc: number;
  sizeBucket: DevexReviewLatencySizeBucket;
  surface: DevexReviewTouchSurface;
  authorIsBot: boolean;
  authorTeamRelationship: DevexReviewAuthorTeamRelationship;
  assignmentMode: DevexReviewAssignmentMode;
  openedHourEt: number;
  openedHourBucket: DevexReviewOpenedHourBucket;
  openedDayOfWeek: number;
  openedDayBucket: DevexReviewOpenedDayBucket;
}

export type DevexCodexFeedbackPullRequestState = 'OPEN' | 'MERGED' | 'CLOSED';
export type DevexCodexFeedbackFactSentiment = 'helpful' | 'notHelpful' | 'unrated';
export const DEVEX_CODEX_FEEDBACK_REPLY_SENTIMENTS = ['accepted', 'rejected', 'mixed', 'question', 'unknown'] as const;
export type DevexCodexFeedbackReplySentiment = (typeof DEVEX_CODEX_FEEDBACK_REPLY_SENTIMENTS)[number];
export const DEVEX_CODEX_FEEDBACK_REJECTION_REASONS = [
  'alreadyHandled',
  'deferredOrOutOfScope',
  'incorrectOrStale',
  'intentionalOrFutureWork',
  'localOrOneOff',
  'lowRiskOrNotNeeded',
  'other',
] as const;
export type DevexCodexFeedbackRejectionReason = (typeof DEVEX_CODEX_FEEDBACK_REJECTION_REASONS)[number];

export interface DevexCodexFeedbackInferredReplyFeedback {
  rejectionReason: DevexCodexFeedbackRejectionReason | null;
  sentiment: DevexCodexFeedbackReplySentiment;
}

export interface DevexCodexFeedbackFactComment {
  authorLogin: string;
  body: string;
  createdAt: string;
  reactions: {
    thumbsDown: number;
    thumbsUp: number;
  };
  updatedAt: string;
  url: string;
}

interface DevexCodexFeedbackFactBaseItem {
  generatedAt: string;
  prAuthorLogin: string;
  prNumber: number;
  prState: DevexCodexFeedbackPullRequestState;
  prTitle: string;
  prUpdatedAt: string;
  prUrl: string;
  threadCommentsTruncated: boolean;
  threadId: string;
  threadIsOutdated: boolean;
  threadIsResolved: boolean;
  threadLine: number | null;
  threadPath: string;
  threadStartLine: number | null;
  codexComment: DevexCodexFeedbackFactComment;
  sentiment: DevexCodexFeedbackFactSentiment;
}

export interface DevexCodexFeedbackCommentFactItem extends DevexCodexFeedbackFactBaseItem {
  pk: 'codex-comment';
  sk: string;
}

export interface DevexCodexFeedbackReplyFactItem extends DevexCodexFeedbackFactBaseItem {
  pk: 'codex-reply';
  sk: string;
  reply: DevexCodexFeedbackFactComment;
  replyAuthorIsBot: boolean;
  rejectionReason?: DevexCodexFeedbackRejectionReason | null;
  replySentiment?: DevexCodexFeedbackReplySentiment;
}

export interface DevexCodexFeedbackSyncFactItem {
  pk: 'sync';
  sk: string;
  commentCount: number;
  coverageStartAt: string;
  generatedAt: string;
  lookbackDays: number;
  replyCount: number;
  sourcePullRequestCount: number;
}

export interface DevexLatencySummary {
  avg: number | null;
  p50: number | null;
  p75: number | null;
  p90: number | null;
  p99: number | null;
}

// Nearest-rank method: returns an actual observed value, no interpolation between samples.
export function devexPercentile(values: number[], rank: number): number | null {
  if (values.length === 0) return null;
  const index = Math.max(0, Math.min(values.length - 1, Math.ceil(rank * values.length) - 1));
  return values[index]!;
}

// Shared so the infra Lambda (pre-aggregated rows) and the dev-console fact path compute latency identically.
export function summarizeDevexLatencies(values: number[]): DevexLatencySummary {
  if (values.length === 0) {
    return { avg: null, p50: null, p75: null, p90: null, p99: null };
  }
  const ordered = [...values].sort((left, right) => left - right);
  const mean = ordered.reduce((acc, value) => acc + value, 0) / ordered.length;
  return {
    avg: Math.round(mean * 100) / 100,
    p50: devexPercentile(ordered, 0.5),
    p75: devexPercentile(ordered, 0.75),
    p90: devexPercentile(ordered, 0.9),
    p99: devexPercentile(ordered, 0.99),
  };
}

const CODEX_REPLY_ACCEPTED_PATTERNS = [
  /\+1\b/i,
  /\b(addressed|added|changed|done|fixed|handled|implemented|patched|removed|renamed|resolved|switched|updated)\b/i,
  /\b(agree|agreed|correct|fair point|good call|good catch|good point|great call|great catch)\b/i,
  /\b(makes sense|nice call|nice catch|seems valid|valid|you are right|you're right)\b/i,
  /\b(appreciate|good callout|helpful|thank you|thanks)\b/i,
  /\b(can do|i'll add|i'll change|i'll fix|i'll move|i'll remove|i'll update|sgtm|sounds good)\b/i,
  /\b(will add|will change|will fix|will move|will remove|will update)\b/i,
  /\b(?:seems|looks|feels|probably|definitely|still|also) worth (?:it|doing|fixing|addressing|changing|updating)\b/i,
  /\btechnically (?:true|correct|valid)\b/i,
];

const CODEX_REPLY_NEGATED_ACCEPTED_PATTERNS = [
  /\b(?:does not seem|doesn['’]t seem|is not|isn['’]t|not)\s+(?:actually\s+|really\s+|technically\s+)?(?:correct|right|true|valid)\b/i,
];

const CODEX_REPLY_REJECTED_PATTERNS = [
  /\b(already (?:covered|doing|handled)|by design|existing behavior|expected behavior)\b/i,
  /\b(false positive|feature,? not a bug|intentional|pre[- ]?existing)\b/i,
  /\b(does not apply|doesn't apply|i do not think|i don't think|not applicable|not concerned)\b/i,
  /\b(not going to|not sure this applies|not worth|outside scope|won't fix|will not fix)\b/i,
  /\b(no change|no need|not an issue|not a concern|not a problem|not a regression|not needed|not necessary|not worth fixing)\b/i,
  /\b(incorrect|misread|misunderstood|outdated|stale|wrong)\b/i,
  /\b(fine for (?:our )?local|local purposes|manual script|one-off|dev only)\b/i,
  /\b(follow[- ]?up|separate pr|upcoming work)\b/i,
  /\b(leave (?:it|this) as is|leave this be|opt to leave|keeping (?:it|this))\b/i,
];

const CODEX_REPLY_QUESTION_PATTERNS = [
  /\?$/,
  /\b(can you clarify|could you|do you think|how would|is this|what do you mean|where|why|wdyt)\b/i,
];

export function inferDevexCodexFeedbackReplyFeedback(body: string): DevexCodexFeedbackInferredReplyFeedback {
  const normalizedBody = normalizeCodexReplyBody(body);
  if (!normalizedBody) {
    return { rejectionReason: null, sentiment: 'unknown' };
  }

  const negatedAccepted = matchesAnyPattern(normalizedBody, CODEX_REPLY_NEGATED_ACCEPTED_PATTERNS);
  const accepted = !negatedAccepted && matchesAnyPattern(normalizedBody, CODEX_REPLY_ACCEPTED_PATTERNS);
  const rejected = negatedAccepted || matchesAnyPattern(normalizedBody, CODEX_REPLY_REJECTED_PATTERNS);
  const question = matchesAnyPattern(normalizedBody, CODEX_REPLY_QUESTION_PATTERNS);
  const rejectionReason = rejected ? inferDevexCodexFeedbackRejectionReason(normalizedBody) : null;

  if (accepted && rejected) {
    return { rejectionReason, sentiment: 'mixed' };
  }
  if (rejected) {
    return { rejectionReason, sentiment: 'rejected' };
  }
  if (question) {
    return { rejectionReason: null, sentiment: 'question' };
  }
  if (accepted) {
    return { rejectionReason: null, sentiment: 'accepted' };
  }
  return { rejectionReason: null, sentiment: 'unknown' };
}

function inferDevexCodexFeedbackRejectionReason(body: string): DevexCodexFeedbackRejectionReason {
  if (/\b(intentional|by design|expected behavior|feature,? not a bug|todo|upcoming work)\b/i.test(body)) {
    return 'intentionalOrFutureWork';
  }
  if (/\b(already (?:covered|doing|handled)|existing behavior|handled here|pre[- ]?existing)\b/i.test(body)) {
    return 'alreadyHandled';
  }
  if (/\b(follow[- ]?up|separate pr|another pr|later|outside scope|revisit)\b/i.test(body)) {
    return 'deferredOrOutOfScope';
  }
  if (/\b(dev only|fine for (?:our )?local|local purposes|manual script|one-off)\b/i.test(body)) {
    return 'localOrOneOff';
  }
  if (
    /\b(no need|not a concern|not an issue|not a problem|not concerned|not necessary|not needed|not worth)\b/i.test(
      body
    )
  ) {
    return 'lowRiskOrNotNeeded';
  }
  if (
    matchesAnyPattern(body, CODEX_REPLY_NEGATED_ACCEPTED_PATTERNS) ||
    /\b(false positive|incorrect|invalid|misread|misunderstood|outdated|stale|wrong)\b/i.test(body)
  ) {
    return 'incorrectOrStale';
  }
  return 'other';
}

function matchesAnyPattern(value: string, patterns: RegExp[]): boolean {
  return patterns.some((pattern) => pattern.test(value));
}

function normalizeCodexReplyBody(body: string): string {
  return body.replace(/\r/g, '').replace(/\s+/g, ' ').trim();
}
